import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Lock, ShieldCheck, Zap, RefreshCw, CheckCircle2,
  AlertTriangle, ShieldAlert, Loader2, Info,
} from "lucide-react";
import { toast } from "sonner";
import { http, PROMO_CODES } from "@/lib/api";
import { Header, Footer, ConfidentialBadge } from "@/components/fc/parts";
import { ProgressBar, ChoiceGroup, YN } from "@/components/fc/inputs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STEPS = ["Le bien concerné", "Votre possession", "Titre & preuves", "Votre résultat"];
const LEVEL = {
  success: { color: "var(--fc-teal)", Icon: CheckCircle2 },
  warn: { color: "var(--fc-warn)", Icon: AlertTriangle },
  danger: { color: "var(--fc-danger)", Icon: ShieldAlert },
};

const empty = {
  territoire: "", type_bien: "", indivision: "", annee_debut: "",
  occ_continue: "", occ_paisible: "", occ_publique: "", occ_proprietaire: "", occ_conteste: "",
  juste_titre: "", bonne_foi: "", documents: [],
};

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-sm font-semibold text-foreground">{label}</Label>
        {hint && <p className="mt-0.5 text-xs" style={{ color: "var(--fc-secondary-text)" }}>{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Question({ n, label, article, hint, value, onChange, testid }) {
  return (
    <div className="space-y-2.5 rounded-2xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-foreground">
          <span className="fc-mono mr-1.5 text-xs" style={{ color: "var(--fc-secondary-text)" }}>{n}.</span>
          {label}
        </p>
        {article && <span className="fc-mono shrink-0 text-[10px]" style={{ color: "var(--fc-secondary-text)" }}>{article}</span>}
      </div>
      {hint && <p className="text-xs" style={{ color: "var(--fc-secondary-text)" }}>{hint}</p>}
      <ChoiceGroup value={value} onChange={onChange} options={YN} name={label} testid={testid} />
    </div>
  );
}

export default function FoncierClair() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(1);
  const [a, setA] = useState(empty);
  const [verdict, setVerdict] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);

  useEffect(() => {
    http.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const toggleDoc = (key) => {
    setA((p) => {
      if (key === "aucun") return { ...p, documents: p.documents.includes("aucun") ? [] : ["aucun"] };
      const docs = p.documents.filter((d) => d !== "aucun");
      return { ...p, documents: docs.includes(key) ? docs.filter((d) => d !== key) : [...docs, key] };
    });
  };

  const year = meta?.current_year || new Date().getFullYear();
  const price = meta?.price_eur || 69;

  const anneeNum = parseInt(a.annee_debut, 10);
  const anneeValid = !isNaN(anneeNum) && anneeNum >= 1900 && anneeNum <= year;
  const duree = anneeValid ? year - anneeNum : null;

  const validation = useMemo(() => {
    if (step === 1) {
      const miss = [];
      if (!a.territoire) miss.push("le territoire");
      if (!a.type_bien) miss.push("le type de bien");
      if (!a.indivision) miss.push("la situation de succession / indivision");
      return { ok: miss.length === 0, msg: miss.length ? `Veuillez renseigner ${miss.join(", ")}.` : "" };
    }
    if (step === 2) {
      if (!a.annee_debut) return { ok: false, msg: "Indiquez l'année de début d'occupation continue du bien." };
      if (!anneeValid) return { ok: false, msg: `L'année doit être comprise entre 1900 et ${year}.` };
      const q = ["occ_continue", "occ_paisible", "occ_publique", "occ_proprietaire", "occ_conteste"];
      const unanswered = q.filter((k) => !a[k]).length;
      if (unanswered) return { ok: false, msg: `Répondez aux ${unanswered} question(s) restante(s) sur votre possession.` };
      return { ok: true, msg: "" };
    }
    if (step === 3) {
      const miss = [];
      if (!a.juste_titre) miss.push("le juste titre");
      if (!a.bonne_foi) miss.push("la bonne foi");
      if (!a.documents.length) miss.push("au moins une réponse sur vos documents (ou « Aucun »)");
      return { ok: miss.length === 0, msg: miss.length ? `Il reste à renseigner : ${miss.join(", ")}.` : "" };
    }
    return { ok: true, msg: "" };
  }, [step, a, anneeValid, year]);

  const goResult = async () => {
    setLoadingResult(true);
    try {
      const payload = { ...a, annee_debut: anneeValid ? anneeNum : null };
      const { data } = await http.post("/evaluate", payload);
      setVerdict(data);
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoadingResult(false);
    }
  };

  const next = () => {
    if (!validation.ok) return;
    if (step === 3) return goResult();
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!started) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Landing onStart={() => setStarted(true)} price={price} />
        <Footer />
      </div>
    );
  }

  if (step === 4 && verdict) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Result verdict={verdict} price={price} onBack={() => setStep(3)} onUnlock={() => setStep(5)} />
        <Footer />
      </div>
    );
  }

  if (step === 5) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Checkout answers={a} anneeNum={anneeValid ? anneeNum : null} price={price} verdict={verdict}
          paymentLink={meta?.payment_link} onBack={() => setStep(4)} navigate={navigate} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <ProgressBar steps={STEPS} current={step} />
        <div className="mt-6">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }} className="space-y-5">
              {step === 1 && (
                <>
                  <StepTitle title="Le bien concerné" desc="Quelques éléments sur le bien que vous occupez." />
                  <Field label="Dans quel territoire se situe le bien ?">
                    <Select value={a.territoire} onValueChange={(v) => set("territoire", v)}>
                      <SelectTrigger data-testid="territoire-select" className="h-12 bg-card">
                        <SelectValue placeholder="Sélectionnez un territoire" />
                      </SelectTrigger>
                      <SelectContent>
                        {meta?.territoires.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`territoire-${t}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="De quel type de bien s'agit-il ?">
                    <ChoiceGroup value={a.type_bien} onChange={(v) => set("type_bien", v)} testid="type-bien"
                      name="Type de bien"
                      options={(meta?.types_bien || []).map((t) => ({ value: t.key, label: t.label }))} />
                  </Field>
                  <Field label="Ce bien fait-il partie d'une succession non réglée ou d'une indivision entre héritiers ?">
                    <ChoiceGroup value={a.indivision} onChange={(v) => set("indivision", v)} testid="indivision" name="Indivision" options={YN} />
                  </Field>
                </>
              )}

              {step === 2 && (
                <>
                  <StepTitle title="Votre possession du bien" desc="Depuis quand et dans quelles conditions occupez-vous le bien ?" />
                  <Field label="Année de début d'occupation continue"
                    hint={`Entre 1900 et ${year}. C'est l'année à partir de laquelle vous occupez le bien sans interruption.`}>
                    <div className="flex items-center gap-3">
                      <Input type="number" inputMode="numeric" min={1900} max={year} value={a.annee_debut}
                        onChange={(e) => set("annee_debut", e.target.value)} placeholder="ex. 1998"
                        data-testid="annee-debut-input" className="h-12 max-w-[180px] bg-card text-lg" />
                      {duree != null && (
                        <span className="fc-mono text-sm" style={{ color: "var(--fc-navy)" }} data-testid="duree-badge">
                          {duree} ans d'occupation
                        </span>
                      )}
                    </div>
                  </Field>
                  <Question n={1} label="Occupez-vous ce bien de façon ininterrompue depuis cette date (sans abandon) ?"
                    article="Art. 2258" value={a.occ_continue} onChange={(v) => set("occ_continue", v)} testid="occ-continue" />
                  <Question n={2} label="Votre possession est-elle paisible (sans violence ni contrainte) ?"
                    article="Art. 2261" value={a.occ_paisible} onChange={(v) => set("occ_paisible", v)} testid="occ-paisible" />
                  <Question n={3} label="Votre possession est-elle publique et visible (non cachée) ?"
                    article="Art. 2261" value={a.occ_publique} onChange={(v) => set("occ_publique", v)} testid="occ-publique" />
                  <Question n={4} label="Vous comportez-vous en propriétaire (entretien, travaux, paiement de la taxe foncière) ?"
                    article="Art. 2255" value={a.occ_proprietaire} onChange={(v) => set("occ_proprietaire", v)} testid="occ-proprietaire" />
                  <Question n={5} label="Votre possession a-t-elle déjà été contestée par un tiers (héritier, voisin, ancien propriétaire) ?"
                    article="Art. 2261" hint="Une contestation rend la possession « équivoque »." value={a.occ_conteste}
                    onChange={(v) => set("occ_conteste", v)} testid="occ-conteste" />
                </>
              )}

              {step === 3 && (
                <>
                  <StepTitle title="Titre et preuves disponibles" desc="Disposez-vous d'un document de propriété et de preuves de votre occupation ?" />
                  <Question n={1} label="Disposez-vous d'un acte de propriété, même imparfait (« juste titre ») ?"
                    hint="Un acte qui vous a transmis le bien, même s'il présente un défaut." article="Art. 2272 al. 2"
                    value={a.juste_titre} onChange={(v) => set("juste_titre", v)} testid="juste-titre" />
                  <Question n={2} label="Pensiez-vous cet acte pleinement valable au moment de l'obtenir (« bonne foi ») ?"
                    value={a.bonne_foi} onChange={(v) => set("bonne_foi", v)} testid="bonne-foi" />
                  <Field label="Quels documents pouvez-vous réunir ?" hint="Cochez tout ce qui s'applique.">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(meta?.documents || []).map((doc) => {
                        const checked = a.documents.includes(doc.key);
                        const disabled = a.documents.includes("aucun");
                        return (
                          <div key={doc.key}
                            onClick={() => !disabled && toggleDoc(doc.key)}
                            className="fc-choice flex cursor-pointer items-start gap-3 rounded-xl border-2 bg-card px-4 py-3 text-sm"
                            style={{ borderColor: checked ? "var(--fc-navy)" : "hsl(var(--border))", opacity: disabled ? 0.5 : 1 }}
                            data-testid={`doc-${doc.key}`}>
                            <Checkbox checked={checked} disabled={disabled} tabIndex={-1} className="pointer-events-none mt-0.5" />
                            <span className="text-foreground">{doc.label}</span>
                          </div>
                        );
                      })}
                      <div onClick={() => toggleDoc("aucun")}
                        className="fc-choice flex cursor-pointer items-start gap-3 rounded-xl border-2 bg-card px-4 py-3 text-sm sm:col-span-2"
                        style={{ borderColor: a.documents.includes("aucun") ? "var(--fc-navy)" : "hsl(var(--border))" }}
                        data-testid="doc-aucun">
                        <Checkbox checked={a.documents.includes("aucun")} tabIndex={-1} className="pointer-events-none mt-0.5" />
                        <span className="font-medium text-foreground">Aucun de ces documents pour l'instant</span>
                      </div>
                    </div>
                  </Field>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          {validation.msg && (
            <div className="mt-5 flex items-start gap-2 rounded-xl px-4 py-3 text-sm" data-testid="help-message"
              style={{ backgroundColor: "var(--fc-surface2)", color: "var(--fc-secondary-text)" }}>
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>{validation.msg}</span>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={prev} disabled={step === 1} data-testid="prev-button"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-5 py-3 text-sm font-medium text-foreground disabled:opacity-40">
              <ArrowLeft size={16} /> Précédent
            </button>
            <button type="button" onClick={next} disabled={!validation.ok || loadingResult} data-testid="next-button"
              className="fc-navy-btn inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold">
              {loadingResult ? <Loader2 size={16} className="animate-spin" /> : null}
              {step === 3 ? "Voir mon résultat" : "Suivant"}
              {!loadingResult && <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function StepTitle({ title, desc }) {
  return (
    <div>
      <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">{title}</h1>
      <p className="mt-1.5 text-sm" style={{ color: "var(--fc-secondary-text)" }}>{desc}</p>
    </div>
  );
}

function Landing({ onStart, price }) {
  return (
    <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="fc-fade-up">
        <ConfidentialBadge />
        <h1 className="mt-5 font-serif text-4xl font-bold leading-tight text-foreground sm:text-5xl">
          Occupez-vous un bien sans en être le propriétaire titré ?
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "var(--fc-secondary-text)" }}>
          En Outre-mer, de nombreuses familles occupent depuis des années un terrain ou une maison issus d'une
          indivision ou d'une succession non réglée. La <strong className="text-foreground">prescription
          acquisitive</strong> (ou <em>usucapion</em>) peut permettre d'en devenir propriétaire. Ce diagnostic
          vous éclaire en quelques minutes, sereinement et sans jargon.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {[
            ["Diagnostic gratuit", "4 étapes guidées, sans engagement."],
            ["Résultat immédiat", "Un verdict clair fondé sur le Code civil."],
            ["Fiche complète", `Synthèse détaillée à ${price} € si vous le souhaitez.`],
          ].map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-border bg-card p-5">
              <div className="font-serif text-lg font-semibold text-foreground">{t}</div>
              <p className="mt-1 text-sm" style={{ color: "var(--fc-secondary-text)" }}>{d}</p>
            </div>
          ))}
        </div>

        <button type="button" onClick={onStart} data-testid="start-button"
          className="fc-navy-btn mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl px-8 py-4 text-base font-semibold sm:w-auto">
          Commencer le diagnostic gratuit <ArrowRight size={18} />
        </button>
        <p className="mt-3 fc-mono text-[11px]" style={{ color: "var(--fc-secondary-text)" }}>
          Aucune carte bancaire requise pour le diagnostic.
        </p>
      </div>
    </main>
  );
}

function Result({ verdict, price, onBack, onUnlock }) {
  const lv = LEVEL[verdict.level];
  return (
    <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
      <ProgressBar steps={STEPS} current={4} />
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: "var(--fc-surface2)" }}>
              <lv.Icon size={24} style={{ color: lv.color }} />
            </div>
            <div>
              <div className="fc-mono text-[11px] uppercase tracking-wider" style={{ color: lv.color }}>Résultat du diagnostic</div>
              <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl" data-testid="verdict-title">{verdict.title}</h1>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-foreground" data-testid="verdict-text">{verdict.text}</p>
        </div>

        <div className="relative mt-6 overflow-hidden rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="fc-teaser-blur space-y-3" aria-hidden="true">
            <div className="font-serif text-xl font-semibold text-foreground">Régime applicable : prescription acquisitive …………</div>
            <div className="fc-mono text-xs" style={{ color: "var(--fc-secondary-text)" }}>Base légale : Art. 22•• al. • du Code civil — Loi n° 20••-••• </div>
            <p className="text-sm text-foreground">
              Avec •• ans de possession déclarée, le délai de •• ans ……………………………… Les documents à réunir en priorité
              sont ……………………………… et la marche à suivre auprès du notaire ………………………………
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-4 rounded" style={{ backgroundColor: "var(--fc-surface2)" }} />
              ))}
            </div>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-card/40 backdrop-blur-[2px]">
            <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: "var(--fc-navy)" }}>
              <Lock size={18} color="#fff" />
            </div>
            <p className="text-sm font-medium text-foreground">Détail précis réservé à la fiche</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border-2 p-6 sm:p-8" style={{ borderColor: "var(--fc-gold)", backgroundColor: "var(--fc-surface2)" }}>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground">Débloquez votre fiche de synthèse</h2>
              <ul className="mt-2 space-y-1 text-sm" style={{ color: "var(--fc-secondary-text)" }}>
                <li>• Régime exact, base légale précise et années calculées</li>
                <li>• Détail des conditions et documents à réunir</li>
                <li>• Fiche PDF envoyée par e-mail + mise en relation partenaire possible</li>
              </ul>
            </div>
            <div className="text-right">
              <div className="font-serif text-3xl font-bold text-foreground">{price} €</div>
              <div className="fc-mono text-[11px]" style={{ color: "var(--fc-secondary-text)" }}>TTC · paiement unique</div>
            </div>
          </div>
          <button type="button" onClick={onUnlock} data-testid="unlock-button"
            className="fc-gold-btn mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-8 py-4 text-base font-bold">
            Débloquer mon résultat ({price} €) <ArrowRight size={18} />
          </button>
        </div>

        <button type="button" onClick={onBack} data-testid="result-back-button"
          className="mt-6 inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--fc-navy)" }}>
          <ArrowLeft size={16} /> Modifier mes réponses
        </button>
      </motion.div>
    </main>
  );
}

function Checkout({ answers, anneeNum, price, verdict, paymentLink, onBack, navigate }) {
  const [f, setF] = useState({
    prenom: "", nom: "", email: "", telephone: "", wants_callback: "",
    renonciation: false, transmission: false, promo: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email);
  const isDemo = PROMO_CODES.includes(f.promo.trim().toUpperCase());
  const phoneNeeded = f.wants_callback === "oui";
  const phoneOk = !phoneNeeded || f.telephone.trim().length >= 6;

  const missing = [];
  if (!f.prenom.trim()) missing.push("votre prénom");
  if (!f.nom.trim()) missing.push("votre nom");
  if (!emailOk) missing.push("une adresse e-mail valide");
  if (!f.wants_callback) missing.push("votre choix de rappel partenaire");
  if (phoneNeeded && !phoneOk) missing.push("un numéro de téléphone pour le rappel");
  if (!f.renonciation) missing.push("l'acceptation de la renonciation au droit de rétractation");
  const canPay = missing.length === 0;

  const submit = async () => {
    if (!canPay) return;
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const { data } = await http.post("/dossiers", {
        answers: { ...answers, annee_debut: anneeNum },
        applicant: {
          prenom: f.prenom.trim(), nom: f.nom.trim(), email: f.email.trim(),
          telephone: f.telephone.trim() || null, wants_callback: f.wants_callback === "oui",
        },
        consents: { renonciation_retractation: f.renonciation, transmission_partenaire: f.transmission },
        promo_code: f.promo.trim() || null,
        origin_url: origin,
      });
      if (data.demo) {
        navigate(`/synthesis/${data.dossier_id}`);
        return;
      }
      if (paymentLink) {
        const url = `${paymentLink}?prefilled_email=${encodeURIComponent(f.email.trim())}&client_reference_id=${data.dossier_id}`;
        window.location.href = url;
      } else {
        toast.error("Lien de paiement indisponible pour le moment. Merci de réessayer.");
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.");
      setSubmitting(false);
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
      <button type="button" onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--fc-navy)" }} data-testid="checkout-back-button">
        <ArrowLeft size={16} /> Retour au résultat
      </button>
      <h1 className="font-serif text-3xl font-semibold text-foreground">Finaliser votre demande</h1>
      <p className="mt-1.5 text-sm" style={{ color: "var(--fc-secondary-text)" }}>
        Vos coordonnées nous permettent de vous adresser votre fiche et, si vous le souhaitez, de vous mettre en relation avec un partenaire.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {[[ShieldCheck, "Paiement sécurisé"], [Zap, "Résultat immédiat par e-mail"], [RefreshCw, "Rappel partenaire sans engagement"]].map(([Ic, t]) => (
          <div key={t} className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-xs font-medium text-foreground">
            <Ic size={16} style={{ color: "var(--fc-teal)" }} /> {t}
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Prénom</Label>
            <Input value={f.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="prenom-input" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Nom</Label>
            <Input value={f.nom} onChange={(e) => set("nom", e.target.value)} data-testid="nom-input" className="h-11" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">E-mail</Label>
          <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="email-input"
            className="h-11" placeholder="vous@exemple.com" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Souhaitez-vous être rappelé(e) par l'un de nos partenaires ?</Label>
          <ChoiceGroup value={f.wants_callback} onChange={(v) => set("wants_callback", v)} testid="callback"
            name="Rappel partenaire" options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} />
        </div>
        {phoneNeeded && (
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Téléphone</Label>
            <Input value={f.telephone} onChange={(e) => set("telephone", e.target.value)} data-testid="telephone-input"
              className="h-11" placeholder="06 90 00 00 00" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Code promo (optionnel)</Label>
          <Input value={f.promo} onChange={(e) => set("promo", e.target.value)} data-testid="promo-input"
            className="h-11 fc-mono uppercase" placeholder="—" />
          {isDemo && (
            <p className="fc-mono text-xs" style={{ color: "var(--fc-teal)" }} data-testid="promo-valid">
              Code démonstration valide — 0 € (démo)
            </p>
          )}
        </div>

        <div className="space-y-3 border-t border-border pt-4">
          <div className="flex cursor-pointer items-start gap-3 text-sm" data-testid="renonciation-checkbox"
            onClick={() => set("renonciation", !f.renonciation)}>
            <Checkbox checked={f.renonciation} tabIndex={-1} className="pointer-events-none mt-0.5" />
            <span style={{ color: "var(--fc-secondary-text)" }}>
              J'accepte que ma fiche numérique soit livrée immédiatement et je renonce expressément à mon droit de
              rétractation de 14 jours (art. L221-28 13° du Code de la consommation). <span className="text-foreground">Obligatoire.</span>
            </span>
          </div>
          <div className="flex cursor-pointer items-start gap-3 text-sm" data-testid="transmission-checkbox"
            onClick={() => set("transmission", !f.transmission)}>
            <Checkbox checked={f.transmission} tabIndex={-1} className="pointer-events-none mt-0.5" />
            <span style={{ color: "var(--fc-secondary-text)" }}>
              J'accepte que mon dossier soit transmis à un partenaire professionnel (notaire ou équivalent) pour un
              éventuel accompagnement. <span className="italic">Facultatif — sans incidence sur l'achat.</span>
            </span>
          </div>
        </div>
      </div>

      {!canPay && (
        <div className="mt-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm" data-testid="checkout-help"
          style={{ backgroundColor: "var(--fc-surface2)", color: "var(--fc-secondary-text)" }}>
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>Pour continuer, veuillez renseigner : {missing.join(", ")}.</span>
        </div>
      )}

      <button type="button" onClick={submit} disabled={!canPay || submitting} data-testid="pay-button"
        className="fc-gold-btn mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-8 py-4 text-base font-bold">
        {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
        {isDemo ? "Accéder à ma fiche (démonstration) →" : `Payer ${price} € et recevoir ma fiche`}
      </button>
      <p className="mt-3 text-center fc-mono text-[11px]" style={{ color: "var(--fc-secondary-text)" }}>
        {isDemo ? "Mode démonstration interne AUPRÉA — aucun paiement réel." : "Paiement traité de manière sécurisée par Stripe."}
      </p>
    </main>
  );
}
